import { useMemo, useState } from 'react';
import {
  useDrillTargets,
  useCreateDrillTarget,
  useUpdateDrillTarget,
  useDeleteDrillTarget,
  type DrillParam,
  type DrillParamKind,
  type DrillTarget,
} from '../../api/drill-targets.js';
import { useReportTemplates } from '../../api/report-templates.js';
import { useDashboards } from '../../api/dashboards.js';

/**
 * Admin page for the tenant drill-through link registry.
 *
 * Authors reference these by id from widgets and {{reportLink}} helpers.
 * The param contract declared here determines what the renderer
 * requires at click time; a target with a required param that isn't
 * mapped from a widget row produces '#' instead of a broken link.
 */
export function DrillTargetsAdminPage(): JSX.Element {
  const { data, isLoading, error } = useDrillTargets();
  const [showNew, setShowNew] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);
  const editTarget = data?.find((t) => t.id === editing) ?? null;

  return (
    <>
      <header className="page-title">
        <div>
          <h1>Drill-through registry</h1>
          <p className="page-title-subtitle">
            Named link destinations used by widgets and HTML templates. Every registered target has
            a declared parameter contract; broken drills are blocked at render time rather than
            silently failing.
          </p>
        </div>
        <div className="page-title-actions">
          <span className="badge badge--neutral">{data?.length ?? 0} targets</span>
          <button type="button" className="btn btn--primary" onClick={() => setShowNew(true)}>
            New target
          </button>
        </div>
      </header>

      {isLoading && <div className="text-dim">Loading targets…</div>}
      {error && <div className="alert alert--danger">Failed to load targets.</div>}
      {data && data.length === 0 && !isLoading && (
        <div className="empty-state">
          <h3>No drill targets yet</h3>
          <p>
            Register a target — a report, a dashboard, or an external URL — so widgets can link to
            it.
          </p>
        </div>
      )}

      {data && data.length > 0 && (
        <table className="table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Kind</th>
              <th>Destination</th>
              <th>Params</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {data.map((t) => (
              <tr key={t.id}>
                <td>
                  <strong>{t.name}</strong>
                  {t.description && <div className="text-dim text-sm">{t.description}</div>}
                </td>
                <td>
                  <span className="badge badge--neutral">{t.targetKind}</span>
                </td>
                <td>
                  <code className="text-xs">{t.targetId ?? t.externalUrl}</code>
                </td>
                <td>
                  {t.paramContract.length === 0 ? (
                    <span className="text-dim text-xs">none</span>
                  ) : (
                    <div className="param-chips">
                      {t.paramContract.map((p) => (
                        <span key={p.name} className="param-chip">
                          {p.name}
                          {p.required && <span className="required">*</span>}
                          <span className="text-dim">:{p.kind}</span>
                        </span>
                      ))}
                    </div>
                  )}
                </td>
                <td className="row" style={{ gap: 4 }}>
                  <button
                    type="button"
                    className="btn btn--ghost btn--sm"
                    onClick={() => setEditing(t.id)}
                  >
                    Edit
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {showNew && <NewDrillTargetDialog onClose={() => setShowNew(false)} />}
      {editTarget && <EditDrillTargetDialog target={editTarget} onClose={() => setEditing(null)} />}
    </>
  );
}

function NewDrillTargetDialog({ onClose }: { onClose(): void }): JSX.Element {
  const create = useCreateDrillTarget();
  const { data: templates } = useReportTemplates();
  const { data: dashboards } = useDashboards();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [kind, setKind] = useState<'template' | 'dashboard' | 'external'>('template');
  const [targetId, setTargetId] = useState<string>('');
  const [externalUrl, setExternalUrl] = useState('');
  const [params, setParams] = useState<DrillParam[]>([]);
  const canSubmit = useMemo(
    () =>
      name.trim().length > 0 &&
      ((kind === 'external' && externalUrl.trim().length > 0) ||
        (kind !== 'external' && targetId.length > 0)),
    [name, kind, targetId, externalUrl],
  );

  function submit(e: React.FormEvent): void {
    e.preventDefault();
    if (!canSubmit) return;
    create.mutate(
      {
        name: name.trim(),
        ...(description ? { description } : {}),
        targetKind: kind,
        ...(kind !== 'external' ? { targetId } : {}),
        ...(kind === 'external' ? { externalUrl } : {}),
        paramContract: params,
      },
      { onSuccess: () => onClose() },
    );
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <header className="modal-header">
          <div>
            <h2>New drill target</h2>
            <div className="modal-subtitle">
              Register a reusable link for widgets and templates.
            </div>
          </div>
          <button type="button" className="btn-icon" onClick={onClose} aria-label="Close">
            ×
          </button>
        </header>
        <form onSubmit={submit}>
          <div className="modal-body stack">
            <label className="field">
              <span className="field-label">Name</span>
              <input
                className="input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="device-detail"
                autoFocus
              />
              <div className="field-help">
                Short slug used from widgets & Handlebars. Must be unique per tenant.
              </div>
            </label>
            <label className="field">
              <span className="field-label">Description (optional)</span>
              <input
                className="input"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Per-device detail report"
              />
            </label>
            <label className="field">
              <span className="field-label">Kind</span>
              <select
                className="select"
                value={kind}
                onChange={(e) => {
                  setKind(e.target.value as typeof kind);
                  setTargetId('');
                }}
              >
                <option value="template">Report template</option>
                <option value="dashboard">Dashboard</option>
                <option value="external">External URL</option>
              </select>
            </label>
            {kind === 'template' && (
              <label className="field">
                <span className="field-label">Target template</span>
                <select
                  className="select"
                  value={targetId}
                  onChange={(e) => setTargetId(e.target.value)}
                >
                  <option value="">(select)</option>
                  {templates?.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name}
                    </option>
                  ))}
                </select>
              </label>
            )}
            {kind === 'dashboard' && (
              <label className="field">
                <span className="field-label">Target dashboard</span>
                <select
                  className="select"
                  value={targetId}
                  onChange={(e) => setTargetId(e.target.value)}
                >
                  <option value="">(select)</option>
                  {dashboards?.map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.name} · {d.layoutKind}
                    </option>
                  ))}
                </select>
              </label>
            )}
            {kind === 'external' && (
              <label className="field">
                <span className="field-label">External URL</span>
                <input
                  type="url"
                  className="input"
                  value={externalUrl}
                  onChange={(e) => setExternalUrl(e.target.value)}
                  placeholder="https://…"
                />
              </label>
            )}
            <ParamContractEditor params={params} onChange={setParams} />
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn--ghost" onClick={onClose}>
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn--primary"
              disabled={!canSubmit || create.isPending}
            >
              {create.isPending ? 'Creating…' : 'Create target'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function EditDrillTargetDialog({
  target,
  onClose,
}: {
  target: DrillTarget;
  onClose(): void;
}): JSX.Element {
  const update = useUpdateDrillTarget();
  const del = useDeleteDrillTarget();
  const [name, setName] = useState(target.name);
  const [description, setDescription] = useState(target.description ?? '');
  const [params, setParams] = useState<DrillParam[]>(target.paramContract);

  function save(): void {
    update.mutate(
      {
        id: target.id,
        patch: { name, description, paramContract: params } as Partial<DrillTarget>,
      },
      { onSuccess: () => onClose() },
    );
  }

  function remove(): void {
    if (
      !confirm(
        `Delete drill target "${target.name}"? Widgets and templates referencing it will show broken links (#).`,
      )
    )
      return;
    del.mutate(target.id, { onSuccess: () => onClose() });
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <header className="modal-header">
          <div>
            <h2>Edit drill target</h2>
            <div className="modal-subtitle">
              Kind + destination are immutable; create a new target to re-route.
            </div>
          </div>
          <button type="button" className="btn-icon" onClick={onClose} aria-label="Close">
            ×
          </button>
        </header>
        <div className="modal-body stack">
          <label className="field">
            <span className="field-label">Name</span>
            <input className="input" value={name} onChange={(e) => setName(e.target.value)} />
          </label>
          <label className="field">
            <span className="field-label">Description</span>
            <input
              className="input"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </label>
          <label className="field">
            <span className="field-label">Destination</span>
            <input className="input" value={target.targetId ?? target.externalUrl ?? ''} disabled />
          </label>
          <ParamContractEditor params={params} onChange={setParams} />
        </div>
        <div className="modal-footer">
          <button
            type="button"
            className="btn btn--ghost"
            onClick={remove}
            disabled={del.isPending}
          >
            Delete
          </button>
          <div style={{ flex: 1 }} />
          <button type="button" className="btn btn--ghost" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn--primary"
            onClick={save}
            disabled={update.isPending}
          >
            {update.isPending ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

function ParamContractEditor({
  params,
  onChange,
}: {
  params: DrillParam[];
  onChange(next: DrillParam[]): void;
}): JSX.Element {
  return (
    <div className="field">
      <span className="field-label">Parameter contract</span>
      <div className="field-help" style={{ marginBottom: 6 }}>
        Declare the query params the target expects. Widgets and templates map their fields to these
        names at bind-time.
      </div>
      {params.length === 0 && (
        <div className="text-dim text-sm">No params — the link is unparameterized.</div>
      )}
      {params.map((p, i) => (
        <div className="row" key={i} style={{ marginBottom: 6, gap: 6, alignItems: 'center' }}>
          <input
            className="input input--sm"
            value={p.name}
            onChange={(e) => {
              const next = [...params];
              next[i] = { ...p, name: e.target.value };
              onChange(next);
            }}
            placeholder="name"
            style={{ width: 160 }}
          />
          <select
            className="select"
            value={p.kind}
            onChange={(e) => {
              const next = [...params];
              next[i] = { ...p, kind: e.target.value as DrillParamKind };
              onChange(next);
            }}
            style={{ width: 120 }}
          >
            <option value="string">string</option>
            <option value="integer">integer</option>
            <option value="date">date</option>
            <option value="boolean">boolean</option>
          </select>
          <label className="field-checkbox">
            <input
              type="checkbox"
              checked={p.required}
              onChange={(e) => {
                const next = [...params];
                next[i] = { ...p, required: e.target.checked };
                onChange(next);
              }}
            />
            <span>required</span>
          </label>
          <button
            type="button"
            className="btn-icon"
            onClick={() => onChange(params.filter((_, j) => j !== i))}
            aria-label="Remove param"
          >
            ×
          </button>
        </div>
      ))}
      <button
        type="button"
        className="btn btn--ghost btn--sm"
        onClick={() =>
          onChange([
            ...params,
            { name: `param_${params.length + 1}`, required: false, kind: 'string' },
          ])
        }
      >
        + Add param
      </button>
    </div>
  );
}
